import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { metricsService } from '@services/api/metricsService';
import './ActivityFeed.css';

interface ActivityItem {
  id: string;
  created_at: string;
  action_type: string;
  entity_type: string | null;
  entity_id: string | null;
  staff_user: {
    first_name: string;
    last_name: string;
  } | null;
}

export const ActivityFeed = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivityFeed();
  }, []);

  const loadActivityFeed = async () => {
    setLoading(true);
    const result = await metricsService.getRecentActivity(10);
    
    if (result.success && result.data) {
      setActivities(result.data);
    }
    
    setLoading(false);
  };

  const getActivityIcon = (actionType: string): string => {
    const icons: Record<string, string> = {
      login: '🔑',
      logout: '🚪',
      view_customer: '👁️',
      create_case: '📋',
      update_case: '✏️',
      close_case: '✅',
      send_sms: '📱',
      assign_case: '👤',
      add_case_note: '📝',
    };
    return icons[actionType] || '•';
  };

  const getActivityDescription = (activity: ActivityItem): string => {
    const staffName = activity.staff_user
      ? `${activity.staff_user.first_name} ${activity.staff_user.last_name}`
      : 'System';

    const actions: Record<string, string> = {
      login: 'logged in',
      logout: 'logged out',
      view_customer: 'viewed customer',
      create_case: 'created case',
      update_case: 'updated case',
      close_case: 'closed case',
      send_sms: 'sent SMS',
      assign_case: 'assigned case',
      add_case_note: 'added note to case',
    };

    const action = actions[activity.action_type] || activity.action_type.replace(/_/g, ' ');
    
    return `${staffName} ${action}`;
  };

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="activity-feed-card">
        <h3 className="activity-feed-title">📝 Recent Activity Feed</h3>
        <p className="text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="activity-feed-card">
      <h3 className="activity-feed-title">📝 Recent Activity Feed</h3>
      
      {activities.length === 0 ? (
        <p className="text-secondary">No recent activity</p>
      ) : (
        <div className="activity-feed-list">
          {activities.map((activity) => {
            const content = (
              <>
                <span className="activity-icon">
                  {getActivityIcon(activity.action_type)}
                </span>
                <span className="activity-time">
                  {getTimeAgo(activity.created_at)}
                </span>
                <span className="activity-separator">|</span>
                <span className="activity-description">
                  {getActivityDescription(activity)}
                </span>
                {activity.entity_id && (
                  <span className="activity-entity-id">
                    {activity.entity_id.slice(0, 8)}
                  </span>
                )}
              </>
            );

            if (activity.entity_type && activity.entity_id) {
              return (
                <Link
                  key={activity.id}
                  to={`/${activity.entity_type}s/${activity.entity_id}`}
                  className="activity-link"
                >
                  {content}
                </Link>
              );
            }

            return (
              <div key={activity.id} className="activity-item">
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

